import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { languageUtils } from '@/utils/languageUtils';
import type { SupportedLanguage } from '@/utils/languageUtils';
import { setAccessToken, setDeviceId } from '@/utils/authStorage';

// Extend the Window interface to include ReactNativeWebView
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

interface WebViewInitData {
  type: 'INIT_WEBVIEW';
  accessToken: string | null;
  deviceId: string | null;
  isNativeWebView: boolean;
  targetRoute: string;
  language?: string | null;
}

interface TokenRefreshMessage {
  type: 'TOKEN_REFRESHED' | 'TOKEN_REFRESH_FAILED';
  accessToken?: string;
  error?: string;
}

const WebViewInit: React.FC = () => {
  const [status, setStatus] = useState<'initializing' | 'success' | 'error'>('initializing');
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    // 1. Notify React Native WebView that the page is ready
    if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
      window.ReactNativeWebView.postMessage('WEBVIEW_READY');
      console.log('WebViewInit: Sent WEBVIEW_READY to React Native');
    }

    // 2. Listen for messages from React Native
    const handleMessage = (event: MessageEvent) => {
      console.log('WebViewInit: Received message event', event.data);
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'INIT_WEBVIEW') {
          const initData: WebViewInitData = data;
          // Store the webview flag
          sessionStorage.setItem('isNativeWebView', initData.isNativeWebView ? 'true' : 'false');

          // Set tokens as cookies (not httpOnly, since JS cannot set httpOnly cookies)
          if (initData.accessToken) {
            setAccessToken(initData.accessToken);
          }
          if (initData.deviceId) {
            setDeviceId(initData.deviceId);
          }
          // Set language cookie if provided
          if (initData.language) {
            languageUtils.setLanguage(initData.language as SupportedLanguage);
            document.cookie = `chalo_language=${initData.language}; path=/; expires=${new Date(Date.now() + 365*24*60*60*1000).toUTCString()}; SameSite=Strict`;
          }

          setStatus('success');
          // Redirect to target route with a full reload to ensure language is picked up
          setTimeout(() => {
            window.location.href = initData.targetRoute;
          }, 500);
        } else if (data.type === 'TOKEN_REFRESHED') {
          // Token refresh is now handled globally in apiService.ts
          console.log('WebViewInit: Token refresh response received (handled globally)');
        } else if (data.type === 'TOKEN_REFRESH_FAILED') {
          // Token refresh failure is now handled globally in apiService.ts
          console.error('WebViewInit: Token refresh failed (handled globally)');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setStatus('error');
      }
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage);    
    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('message', handleMessage);
    };
  }, [navigate]);

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Initialization Failed</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
              <div
                  className="rounded-full border-t-[4px] border-r-[4px] border-b-[4px] border-l-[4px] border-t-transparent border-r-transparent border-b-[#400F46] border-l-[#400F46] w-9 h-9 animate-spin mx-auto mb-4"
                  style={{
                      borderTopColor: 'transparent',
                      borderRightColor: 'transparent',
                      borderBottomColor: '#400F46',
                      borderLeftColor: '#400F46'
                  }}
              ></div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {/*{status === 'initializing' ? 'Initializing...' : 'Success!'}*/}
        </h2>
        <p className="text-gray-600">
          {/*{status === 'initializing'*/}
          {/*  ? 'Setting up your session...'*/}
          {/*  : 'Redirecting to your destination...'*/}
          {/*}*/}
        </p>
      </div>
    </div>
  );
};

export default WebViewInit;